<script setup lang="ts">
import { inject, ref, watch } from 'vue';
import DocumentMasterApi from '@/modules/document/api/document-master-api.ts';
import { useMasterFormStore } from '@/modules/document/stores/master-form.ts';
import { LINK_TYPES_IDS } from '@/providers/link-types-ids.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import type { TableId } from '@/modules/common/enums/table-id.enum.ts';
import UiCombobox from '@/modules/ui/components/ui/UiCombobox.vue';

const { index, linkType, linkTypeTableId, isLinkTypeInitial } = defineProps<{
  index: number;
  linkType: Option;
  linkTypeTableId: TableId;
  isLinkTypeInitial: boolean;
}>();

const emit = defineEmits<{
  change: [{ linkType: Option; isLinkTypeInitial: boolean }];
}>();

const masterFormStore = useMasterFormStore();
const getLinkTypesIds = inject(LINK_TYPES_IDS);

const options = ref<Option[]>([]);

async function getLinkTypes(signal?: AbortSignal) {
  const linkTypesIds = (await getLinkTypesIds?.()) ?? masterFormStore.type.linkTypesIds;
  return await DocumentMasterApi.getLinkTypes(linkTypesIds, linkTypeTableId, signal);
}

function selectLinkType(selected: Option) {
  emit('change', { linkType: selected, isLinkTypeInitial: false });
}

function applyInitialLinkType() {
  if (!isLinkTypeInitial || !options.value.length) {
    return;
  }

  const realTypes = options.value.filter((option) => option !== DocumentMasterApi.DEFAULT_LINK_TYPE);
  const initialLinkType = realTypes[index] ?? DocumentMasterApi.DEFAULT_LINK_TYPE;

  if (initialLinkType.code !== linkType.code) {
    emit('change', { linkType: initialLinkType, isLinkTypeInitial: true });
  }
}

function onLoad(loaded: Option[]) {
  options.value = loaded;
  applyInitialLinkType();
}
watch(() => index, applyInitialLinkType);
</script>

<template>
  <UiCombobox
    class="card-link-types"
    color="dark-gray"
    :options="getLinkTypes"
    no-clear
    :model-value="linkType"
    @update:model-value="selectLinkType as any"
    @load="onLoad as any"
  />
</template>
